import { useMemo } from 'react';
import { Briefcase, CalendarCheck2, CalendarClock, CalendarX2 } from 'lucide-react';
import { Chip } from '@vnetwork/ui/primitives';
import { EmptyState, StatusPill } from '@vnetwork/ui/data';
import { useMyBusinesses, useBusinessSummary, type MyBusiness } from '@/api/account-lists';
import { formatDate, formatWeekdayDateTime } from '@/lib/format';
import { statusTone } from './components/status-tone';
import { AccountListScaffold, type ScaffoldColumn } from './components/AccountListScaffold';
import { AccountStatCards } from './components/AccountStatCards';

const code = (b: MyBusiness) => `IBN-${b.business_id}`;
const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '—');
const STATUS_OPTIONS = ['approved', 'pending', 'rejected', 'cancelled'].map((s) => ({ value: s, label: cap(s) }));

/** Business Accounts — trip summary + filterable list (mirrors legacy CÔNG TÁC). */
export default function BusinessesPage() {
  const { data, isLoading, isError, refetch } = useMyBusinesses({ page: 1, page_size: 50 });
  const { data: summary } = useBusinessSummary();
  const rows = data?.results ?? [];

  const typeOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.business_type).filter(Boolean))).map((t) => ({ value: t, label: cap(t) })),
    [rows],
  );

  const banner = (
    <AccountStatCards
      items={[
        { icon: <CalendarCheck2 size={16} />, label: 'Approved', value: summary?.approved_total ?? '—', tone: 'var(--vn-success-solid)' },
        { icon: <CalendarClock size={16} />, label: 'Pending approval', value: summary?.pending_total ?? '—', tone: 'var(--vn-warning-solid)' },
        { icon: <CalendarX2 size={16} />, label: 'Rejected', value: summary?.rejected_total ?? '—', tone: 'var(--vn-danger-solid)' },
      ]}
    />
  );

  const columns = useMemo<ScaffoldColumn<MyBusiness>[]>(() => [
    { id: 'code', header: 'Code', cell: ({ row }) => code(row.original) },
    { id: 'type', header: 'Type', cell: ({ row }) => cap(row.original.business_type) },
    {
      id: 'dates', header: 'Business date',
      cell: ({ row }) => (
        <Chip size="sm" tone="success">{formatWeekdayDateTime(row.original.business_startdate)} – {formatWeekdayDateTime(row.original.business_enddate)}</Chip>
      ),
    },
    { id: 'place', header: 'Location', accessorKey: 'business_place', cell: ({ getValue }) => String(getValue() ?? '—') },
    { id: 'content', header: 'Content', accessorKey: 'business_content', cell: ({ getValue }) => String(getValue() ?? '—') },
    { id: 'created', header: 'Created', cell: ({ row }) => formatDate(row.original.business_created_at) },
    {
      id: 'status', header: 'Status',
      cell: ({ row }) => <StatusPill tone={statusTone(row.original.business_status)}>{row.original.business_status || '—'}</StatusPill>,
    },
  ], []);

  return (
    <AccountListScaffold
      title="Business Accounts"
      breadcrumbLabel="Businesses"
      description="Your business-trip records"
      banner={banner}
      searchPlaceholder="Search business trips"
      rows={rows}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      getRowId={(r) => String(r.business_id)}
      columns={columns}
      searchText={(r) => `${code(r)} ${r.business_type} ${r.business_place} ${r.business_content} ${r.business_status}`}
      filterFields={[
        { value: 'type', label: 'Type', input: 'select', options: typeOptions },
        { value: 'status', label: 'Status', input: 'select', options: STATUS_OPTIONS },
        { value: 'place', label: 'Location' },
      ]}
      filterAccessor={(r, field) =>
        field === 'type' ? r.business_type : field === 'status' ? r.business_status : field === 'place' ? r.business_place : ''
      }
      emptyState={<EmptyState icon={<Briefcase size={24} />} title="No business trips" />}
    />
  );
}
